import { config } from "../config";
import { ExtraParams, OverwrittenParams, UrlParams } from "../types";
import { moduleSettings } from "../stores/module-settings.store";
import { Moment } from "moment";
import moment from "moment";

class RequestParamsService {
  private _referralKey: string;
  private _restaurantId: string;
  private _version?: string;
  private _token?: string;
  private _bookingId?: string;
  private _bookingToken?: string;
  private _preview?: string;

  constructor(
    referralKey: string,
    restaurantId: string,
    version?: string,
    bookingId?: string,
    bookingToken?: string,
    preview?: string
  ) {
    this._referralKey = referralKey;
    this._restaurantId = restaurantId;
    this._version = version;
    this._bookingId = bookingId;
    this._bookingToken = bookingToken;
    this._preview = preview;
  }

  public getParams(): {
    referralKey: string;
    restaurantId: string;
    version?: string;
    bookingId?: string;
    bookingToken?: string;
    preview?: string;
  } {
    return {
      referralKey: this._referralKey,
      restaurantId: this._restaurantId,
      version: this._version,
      bookingId: this._bookingId,
      bookingToken: this._bookingToken,
      preview: this._preview,
    };
  }

  public getModuleToken(): string {
    return this._token || config.token;
  }

  public set restaurantId(restaurantId: string) {
    this._restaurantId = restaurantId;
  }
  public set referralKey(referralKey: string) {
    this._referralKey = referralKey;
  }
}

class RequestParamsImpl {
  private _requestParamsService: RequestParamsService;
  private _overwrittenParams: OverwrittenParams;
  private _hashParams: OverwrittenParams;

  //!\TODO check if params are requested
  requestParamsService(
    referralKey?: string,
    restaurantId?: string,
    version?: string
  ): RequestParamsService {
    if (!this._requestParamsService && referralKey && restaurantId) {
      this._requestParamsService = new RequestParamsService(
        referralKey,
        restaurantId,
        version
      );
    }
    return this._requestParamsService;
  }
  set(
    urlParams: UrlParams,
    searchParams?: URLSearchParams | undefined,
    hashParams?: URLSearchParams
  ) {
    let bookingToken,
      preview = undefined;
    if (searchParams !== undefined) {
      bookingToken = searchParams.get("booking_token") || undefined;
      preview = searchParams.get("preview") || undefined;
    }
    let referralKey = urlParams.referralKey;
    let restaurantId = urlParams.restaurantId;
    if (
      moduleSettings &&
      moduleSettings.settingsAreDefined &&
      referralKey == null
    ) {
      //!\TODO on update_from email referal_key is not setted
      //!\ hence if moduleSettings.settings == {}
      //!\ moduleSetting.getReferralKey may since settings.referak_key is undefined
      referralKey = moduleSettings.getReferralKey();
      restaurantId = moduleSettings.getRestaurantId();
    }
    this._overwrittenParams = this.buildOverwrittenParams(searchParams);
    if (hashParams) {
      this._hashParams = this.buildHashParams(hashParams);
    }
    this._requestParamsService = new RequestParamsService(
      referralKey,
      restaurantId,
      urlParams.version,
      urlParams.bookingId,
      bookingToken,
      preview
    );
  }

  get requestParams(): {
    referralKey: string;
    restaurantId: string;
    version?: string;
    bookingId?: string;
    bookingToken?: string;
    preview?: string;
  } {
    return this.requestParamsService().getParams();
  }

  set restaurantId(restaurantIdValue: string) {
    this.requestParamsService().restaurantId = restaurantIdValue;
  }

  set referralKey(referralKeyValue: string) {
    this.requestParamsService().referralKey = referralKeyValue;
  }

  overwrittenPax(): number | undefined {
    let paxNumber: number | undefined = undefined;
    if (this._hashParams?.pax) {
      paxNumber = Number(this._hashParams.pax);
    } else if (this._overwrittenParams?.pax) {
      paxNumber = Number(this._overwrittenParams?.pax);
    } else if (this._overwrittenParams?.persons) {
      paxNumber = Number(this._overwrittenParams.persons);
    }
    return paxNumber
      ? Number.isNaN(paxNumber)
        ? undefined
        : paxNumber
      : undefined;
  }

  overwrittenNote(): string | undefined {
    return this._overwrittenParams.note;
  }

  //!\moment is tz
  //ex: Fri Jul 01 2022 00:00:00 GMT+0200
  overwrittenDate(): Moment | undefined {
    let date = undefined;

    if (this._hashParams?.date) {
      date = this._hashParams.date;
    } else if (this._overwrittenParams?.date) {
      date = this._overwrittenParams?.date;
    }
    let parsedMoment = date ? moment(date) : undefined;
    return parsedMoment?.isValid() ? parsedMoment : undefined;
  }

  overwrittenLocale(): string | undefined {
    return this._overwrittenParams
      ? this.getLocaleForOverwrittenParams()
      : undefined;
  }

  getLocaleForOverwrittenParams(): string | undefined {
    let _locale = this._overwrittenParams.locale?.toLocaleLowerCase();
    if (_locale === "et") {
      return "ee";
    }
    if (_locale === "jp") {
      return "ja";
    }
    return _locale;
  }

  overwrittenArea(): string | undefined {
    return this._overwrittenParams
      ? this._overwrittenParams.area_id
      : undefined;
  }

  overwrittenTime(): string | undefined {
    if (this._hashParams?.time) {
      return this._hashParams.time;
    }
    if (this._overwrittenParams?.time) {
      return this._overwrittenParams?.time;
    }
    return undefined;
  }

  overwrittenFirstName(): string | undefined {
    if (this._hashParams?.first_name) {
      return this._hashParams.first_name;
    }
    if (this._overwrittenParams?.first_name) {
      return this._overwrittenParams?.first_name;
    }
    return undefined;
  }

  overwrittenLastName(): string | undefined {
    if (this._hashParams?.last_name) {
      return this._hashParams.last_name;
    }
    if (this._overwrittenParams?.last_name) {
      return this._overwrittenParams?.last_name;
    }
    return undefined;
  }

  overwrittenEmail(): string | undefined {
    if (this._hashParams?.email) {
      return this._hashParams.email;
    }
    if (this._overwrittenParams?.email) {
      return this._overwrittenParams?.email;
    }
    return undefined;
  }

  overwrittenCountryCode(): string | undefined {
    if (this._hashParams?.country_code) {
      return this._hashParams.country_code;
    }
    if (this._overwrittenParams?.country_code) {
      return this._overwrittenParams?.country_code;
    }
    return undefined;
  }

  overwrittenMobilePhone(): string | undefined {
    if (this._hashParams?.mobile_phone) {
      return this._hashParams.mobile_phone;
    }
    if (this._overwrittenParams?.mobile_phone) {
      return this._overwrittenParams?.mobile_phone;
    }
    return undefined;
  }

  overwrittenBackgroundColor(): string | undefined {
    return this._overwrittenParams?.background_color
      ? this._overwrittenParams.background_color
      : undefined;
  }

  overwrittenHideGeneralTerms(): boolean {
    return this._overwrittenParams?.hide_general_terms === "true";
  }

  computeSearchQueryForOverwrittenParams(): string | undefined {
    let searchQuery: string | undefined;
    let _this = this;
    type ObjectKey = keyof typeof _this._overwrittenParams;
    Object.entries(ExtraParams).forEach((enumEntry) => {
      const key = enumEntry[0];
      const value = enumEntry[1];
      const extraParamsKey = key as ObjectKey;
      const info = _this._overwrittenParams[extraParamsKey];
      if (info) {
        searchQuery
          ? (searchQuery += `&${value}=${info}`)
          : (searchQuery = `?${value}=${info}`);
      }
    });
    return searchQuery;
  }

  buildOverwrittenParams(
    searchParams?: URLSearchParams | undefined
  ): OverwrittenParams {
    let overwrittenParams: OverwrittenParams = {} as OverwrittenParams;
    if (searchParams !== undefined) {
      Object.entries(ExtraParams).forEach((enumEntry) => {
        const key = enumEntry[0];
        const value = enumEntry[1];
        let searchValue = searchParams!.get(value);
        if (searchValue !== null) {
          overwrittenParams = overwrittenParams
            ? {
                ...overwrittenParams,
                [key]: searchValue,
              }
            : { [key]: searchValue };
        }
      });
    }
    return overwrittenParams;
  }

  buildHashParams(hashParams: URLSearchParams): OverwrittenParams {
    let overwrittenParams: OverwrittenParams = {} as OverwrittenParams;
    Object.entries(ExtraParams).forEach((enumEntry) => {
      const key = enumEntry[0];
      const value = enumEntry[1];
      let searchValue = hashParams!.get(value);
      if (searchValue !== null) {
        overwrittenParams = {
          ...overwrittenParams,
          [key]: searchValue,
        };
      }
    });
    return overwrittenParams;
  }
}

export const requestParamsImpl = new RequestParamsImpl();
